const chapter = {
  id: 'neural-control-coordination',
  slug: 'neural-control-coordination',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Neural Control and Coordination',
        hindi: 'तन्त्रिका नियन्त्रण और समन्वय',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Human Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'neuron',
      title: {
        english: 'Neuron — Structural & Functional Unit',
        hindi: 'तन्त्रिका कोशिका — संरचनात्मक और कार्यात्मक इकाई',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Highly specialised cell — receives, conducts, transmits impulses.',
        hindi: 'अत्यधिक विशिष्ट कोशिका - आवेगों को प्राप्त करती है, संचालित करती है, संचारित करती है।',
            },
            {
              english: 'Parts: **cell body (cyton/soma)** containing nucleus + Nissl granules; **dendrites** (short, many; receive); **axon** (single, long; transmits away).',
        hindi: 'भाग: **कोशिका शरीर (cyton/soma)** जिसमें केन्द्रक + निस्सल कणिकाएँ होती हैं; **डेंड्राइट** (संक्षिप्त, अनेक; प्राप्त); **अक्षतंतु** (एकल, लंबा; दूर संचारित)।',
            },
            {
              english: 'Axon may be **myelinated** (Schwann cells form sheath; nodes of Ranvier in PNS; oligodendrocytes in CNS) or **non-myelinated**.',
        hindi: 'एक्सॉन **माइलिनेटेड** हो सकता है (श्वान कोशिकाएं म्यान बनाती हैं; पीएनएस में रैनवियर के नोड्स; सीएनएस में ऑलिगोडेंड्रोसाइट्स) या **नॉन-माइलिनेटेड**।',
            },
            {
              english: 'Types by structure: unipolar (embryos), **bipolar** (retina, olfactory), **multipolar** (most — cerebrum, spinal cord).',
        hindi: 'संरचना के अनुसार प्रकार: एकध्रुवीय (भ्रूण), **द्विध्रुवी** (रेटिना, घ्राण), **बहुध्रुवीय** (अधिकांश - मस्तिष्क, रीढ़ की हड्डी)।',
            },
            {
              english: 'Types by function: sensory (afferent), motor (efferent), interneuron (associative).',
        hindi: 'फ़ंक्शन के अनुसार प्रकार: संवेदी (अभिवाही), मोटर (अपवाही), इंटिरियरॉन (सहयोगी)। **विश्राम क्षमता** ≈ −70 mV (अंदर नकारात्मक)। **Na⁺/K⁺ पंप** द्वारा बनाए रखा गया (3 Na⁺ बाहर, 2 K⁺ प्रति ATP) + चयनात्मक पारगम्यता (झिल्ली आराम के समय Na⁺ की तुलना में K⁺ के लिए अधिक पारगम्य है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'impulse',
      title: {
        english: 'Generation & Conduction of Nerve Impulse',
        hindi: 'तन्त्रिका आवेग का उत्पन्न और संचरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Resting potential** ≈ −70 mV (inside negative). Maintained by **Na⁺/K⁺ pump** (3 Na⁺ out, 2 K⁺ in per ATP) + selective permeability (membrane more permeable to K⁺ than Na⁺ at rest).',
        hindi: '**विश्राम क्षमता** ≈ −70 mV (नकारात्मक अंदर)। **Na⁺/K⁺ पंप** द्वारा बनाए रखा गया (3 Na⁺ बाहर, 2 K⁺ प्रति ATP) + चयनात्मक पारगम्यता (झिल्ली आराम के समय Na⁺ की तुलना में K⁺ के लिए अधिक पारगम्य है)।',
            },
            {
              english: '**Stimulus** depolarises membrane → if reaches **threshold (~−55 mV)**, voltage-gated **Na⁺ channels open** → Na⁺ rushes in → membrane reverses to ~+30 mV (**depolarisation / action potential**).',
        hindi: '**उत्तेजना** झिल्ली को विध्रुवित करती है → यदि **दहलीज (~−55 mV)** तक पहुंचती है, तो वोल्टेज-गेटेड **Na⁺ चैनल खुलते हैं** → Na⁺ अंदर चला जाता है → झिल्ली ~+30 mV (**विध्रुवण / क्रिया क्षमता**) पर उलट जाती है।',
            },
            {
              english: 'Then Na⁺ channels close, **K⁺ channels open** → K⁺ exits → returns to negative (**repolarisation**); brief **hyperpolarisation** before resting state restored by Na⁺/K⁺ pump.',
        hindi: 'फिर Na⁺ चैनल बंद हो जाते हैं, **K⁺ चैनल खुलते हैं** → K⁺ बाहर निकलते हैं → नकारात्मक पर लौट आते हैं (**पुनर्ध्रुवीकरण**); Na⁺/K⁺ पंप द्वारा आराम की स्थिति बहाल करने से पहले संक्षिप्त **हाइपरपोलराइजेशन**।',
            },
            {
              english: '**All-or-none** principle. Refractory period prevents backward propagation.',
        hindi: '**सभी या कोई नहीं** सिद्धांत। दुर्दम्य अवधि पिछड़े प्रसार को रोकती है।',
            },
            {
              english: '**Conduction velocity**:',
        hindi: '**चालन वेग**:',
            },
            {
              english: '• Non-myelinated — slow, continuous wave.',
        hindi: '• नॉन-माइलिनेटेड - धीमी, निरंतर तरंग।',
            },
            {
              english: '• Myelinated — **saltatory conduction** (jumps node to node); much faster (~100 m/s).',
        hindi: '• माइलिनेटेड - **नमकीय चालन** (नोड से नोड तक कूदता है); बहुत तेज़ (~100 m/s)।',
            },
            {
              english: '**Synapse** — junction between two neurons:',
        hindi: '**सिनैप्स** — दो न्यूरॉन्स के बीच जंक्शन:',
            },
            {
              english: '• **Electrical** — direct ionic flow via gap junctions; very fast; rare in mammals.',
        hindi: '• **विद्युत** - अंतराल जंक्शनों के माध्यम से प्रत्यक्ष आयनिक प्रवाह; बहुत तेज; स्तनधारियों में दुर्लभ.',
            },
            {
              english: '• **Chemical** — synaptic vesicles release **neurotransmitter** (acetylcholine, GABA, dopamine, serotonin, glutamate). Crosses synaptic cleft → binds receptors → opens ion channels → new impulse. Unidirectional.',
        hindi: '• **रासायनिक** - सिनैप्टिक वेसिकल्स **न्यूरोट्रांसमीटर** (एसिटाइलकोलाइन, जीएबीए, डोपामाइन, सेरोटोनिन, ग्लूटामेट) छोड़ते हैं। सिनैप्टिक फांक को पार करता है → रिसेप्टर्स को बांधता है → आयन चैनल खोलता है → नया आवेग। एकदिशात्मक.',
            },
          ],
        },
      ],
    },
    {
      id: 'cns',
      title: {
        english: 'Central Nervous System — Brain',
        hindi: 'केन्द्रीय तन्त्रिका तंत्र — मस्तिष्क',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Brain weighs ~1300-1400 g; protected by **cranium** + **3 meninges** (dura mater, arachnoid, pia mater) + **CSF (cerebrospinal fluid)** in ventricles.',
        hindi: 'मस्तिष्क का वजन ~1300-1400 ग्राम होता है; निलय में **कपाल** + **3 मेनिन्जेस** (ड्यूरा मेटर, अरचनोइड, पिया मेटर) + **CSF (मस्तिष्कमेरु द्रव)** द्वारा संरक्षित।',
            },
            {
              english: 'Three regions: **forebrain, midbrain, hindbrain**.',
        hindi: 'तीन क्षेत्र: **अग्रमस्तिष्क, मध्यमस्तिष्क, पश्चमस्तिष्क**।',
            },
            {
              english: '**Forebrain**:',
        hindi: '**अग्रमस्तिष्क**:',
            },
            {
              english: '• **Cerebrum** — largest part; two hemispheres connected by **corpus callosum**. Outer **cortex (grey matter)** = neuron bodies; inner **white matter** = axons. Cortex has gyri & sulci → ↑ surface area. Lobes: frontal (motor & decision), parietal (sensory), temporal (auditory & memory), occipital (vision).',
        hindi: '• **सेरेब्रम** — सबसे बड़ा भाग; **कॉर्पस कैलोसम** से जुड़े दो गोलार्ध। बाहरी **कॉर्टेक्स (ग्रे मैटर)** = न्यूरॉन निकाय; आंतरिक **श्वेत पदार्थ** = अक्षतंतु। कॉर्टेक्स में ग्यारी और सुल्की → ↑ सतह क्षेत्र है। लोब: ललाट (मोटर और निर्णय), पार्श्विका (संवेदी), टेम्पोरल (श्रवण और स्मृति), पश्चकपाल (दृष्टि)।',
            },
            {
              english: '• **Thalamus** — relay centre for sensory & motor signals (except smell).',
        hindi: '• **थैलेमस** - संवेदी और मोटर सिग्नल (गंध को छोड़कर) के लिए रिले केंद्र।',
            },
            {
              english: '• **Hypothalamus** — at base; controls hunger, thirst, temperature, sleep, emotions, autonomic functions; secretes hormones (releasing & inhibiting).',
        hindi: '• **हाइपोथैलेमस** — आधार पर; भूख, प्यास, तापमान, नींद, भावनाओं, स्वायत्त कार्यों को नियंत्रित करता है; हार्मोन स्रावित करता है (जारी करना और रोकना)।',
            },
            {
              english: '• **Limbic system / lobe** — amygdala + hippocampus + others; emotion, motivation, memory.',
        hindi: '• **लिम्बिक सिस्टम/लोब** — एमिग्डाला + हिप्पोकैम्पस + अन्य; भावना, प्रेरणा, स्मृति.',
            },
            {
              english: '**Midbrain** — between thalamus and pons. Has corpora quadrigemina (visual & auditory reflexes — 2 superior colliculi for vision, 2 inferior for hearing).',
        hindi: '**मिडब्रेन** - थैलेमस और पोंस के बीच। इसमें कॉर्पोरा क्वाड्रिजेमिना (दृश्य और श्रवण संबंधी सजगता - दृष्टि के लिए 2 बेहतर कोलिकुली, सुनने के लिए 2 निम्न) हैं।',
            },
            {
              english: '**Hindbrain**:',
        hindi: '**हिंडब्रेन**:',
            },
            {
              english: '• **Pons** — connects medulla & cerebellum; pneumotaxic centre (regulates breathing).',
        hindi: '• **पॉन्स** — मज्जा और सेरिबैलम को जोड़ता है; न्यूमोटैक्सिक सेंटर (श्वास को नियंत्रित करता है)।',
            },
            {
              english: '• **Cerebellum** — coordination of voluntary movements, balance, posture.',
        hindi: '• **सेरिबैलम** - स्वैच्छिक आंदोलनों, संतुलन, मुद्रा का समन्वय।',
            },
            {
              english: '• **Medulla oblongata** — controls respiration, cardiovascular reflexes, gastric secretions; continues as spinal cord.',
        hindi: '• **मेडुला ऑबोंगटा** - श्वसन, हृदय संबंधी सजगता, गैस्ट्रिक स्राव को नियंत्रित करता है; रीढ़ की हड्डी के रूप में जारी है.',
            },
            {
              english: '**Brainstem** = midbrain + pons + medulla.',
        hindi: '**ब्रेनस्टेम** = मिडब्रेन + पोंस + मेडुला।',
            },
          ],
        },
      ],
    },
    {
      id: 'pns-anc',
      title: {
        english: 'PNS, Reflex Arc & Autonomic NS',
        hindi: 'PNS, प्रतिवर्त चाप और स्वायत्त तंत्रिका तंत्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Spinal cord** — extends from medulla to L1; protected by vertebrae + meninges. Centre of cross-section = grey matter ("H"-shaped); periphery = white matter. **Spinal nerves**: 31 pairs.',
        hindi: '**रीढ़ की हड्डी** - मज्जा से एल1 तक फैली हुई है; कशेरुक + मेनिन्जेस द्वारा संरक्षित। क्रॉस का केंद्र-section = grey matter ("H"-shaped); periphery = white matter. **रीढ़ की नसें**: 31 जोड़े।',
            },
            {
              english: '**Cranial nerves**: 12 pairs from brain.',
        hindi: '**कपाल तंत्रिकाएँ**: मस्तिष्क से 12 जोड़ी।',
            },
            {
              english: '**PNS** divides into:',
        hindi: '**पीएनएस** में विभाजित है:',
            },
            {
              english: '• **Somatic** — skeletal muscle; voluntary.',
        hindi: '• **दैहिक** — कंकालीय मांसपेशी; स्वैच्छिक।',
            },
            {
              english: '• **Autonomic (ANS)** — visceral organs; involuntary. Two divisions:',
        hindi: '• **स्वायत्त (एएनएस)** — आंत के अंग; अनैच्छिक. दो प्रभाग:',
            },
            {
              english: '  – **Sympathetic** — "fight or flight"; thoraco-lumbar; ↑ HR, dilates pupils, ↓ digestion. Neurotransmitter: noradrenaline (post-ganglionic).',
        hindi: '- **सहानुभूति** - "लड़ो या भागो"; थोरैको-लम्बर; ↑ एचआर, पुतलियों को फैलाता है, ↓ पाचन। न्यूरोट्रांसमीटर: नॉरएड्रेनालाईन (पोस्ट-गैंग्लिओनिक)।',
            },
            {
              english: '  – **Parasympathetic** — "rest & digest"; cranio-sacral; ↓ HR, constricts pupils, ↑ digestion. Neurotransmitter: acetylcholine.',
        hindi: '- **पैरासिम्पेथेटिक** - "आराम करो और पचाओ"; कपाल-त्रिक; ↓ एचआर, पुतलियों को संकुचित करता है, ↑ पाचन। न्यूरोट्रांसमीटर: एसिटाइलकोलाइन.',
            },
            {
              english: '**Reflex action** — automatic involuntary response. Reflex arc = receptor → afferent (sensory) neuron → CNS (interneuron in spinal cord) → efferent (motor) neuron → effector. Bypasses conscious brain → very fast.',
        hindi: '**प्रतिवर्ती क्रिया** — स्वचालित अनैच्छिक प्रतिक्रिया। रिफ्लेक्स आर्क = रिसेप्टर → अभिवाही (संवेदी) न्यूरॉन → सीएनएस (रीढ़ की हड्डी में इंटिरियरॉन) → अपवाही (मोटर) न्यूरॉन → इफ़ेक्टर। चेतन मस्तिष्क को बायपास करता है → बहुत तेजी से।',
            },
            {
              english: 'Examples: knee-jerk, withdrawal of hand from hot object, blinking.',
        hindi: 'उदाहरण: घुटने को झटका देना, गर्म वस्तु से हाथ हटाना, पलकें झपकाना।',
            },
          ],
        },
      ],
    },
    {
      id: 'sense-organs',
      title: {
        english: 'Sense Organs (Eye & Ear)',
        hindi: 'ज्ञानेन्द्रियाँ (नेत्र और कर्ण)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Eye**:',
        hindi: '**आँख**:',
            },
            {
              english: '• Three layers: **sclera** (outer protective; cornea anteriorly), **choroid** (middle vascular; iris with pupil + ciliary body + lens), **retina** (inner photosensitive).',
        hindi: '• तीन परतें: **श्वेतपटल** (बाहरी सुरक्षात्मक; कॉर्निया पूर्वकाल में), **कोरॉइड** (मध्य संवहनी; पुतली + सिलिअरी बॉडी + लेंस के साथ परितारिका), **रेटिना** (आंतरिक प्रकाश संवेदनशील)।',
            },
            {
              english: '• Retina has **rods** (~120 million; rhodopsin = vit A + opsin; dim/black-white vision) and **cones** (~6.5 million; iodopsin; colour & sharp vision; concentrated at **fovea**). **Blind spot** = no photoreceptors (where optic nerve exits).',
        hindi: '• रेटिना में **रॉड्स** (~120 मिलियन; rhodopsin = vit A + opsin; dim/black-श्वेत दृष्टि) और **शंकु** (~6.5 मिलियन; आयोडोप्सिन; रंग और तीक्ष्ण दृष्टि; **फोविया** पर केंद्रित) होते हैं। **ब्लाइंड स्पॉट** = कोई फोटोरिसेप्टर नहीं (जहां ऑप्टिक तंत्रिका बाहर निकलती है)।',
            },
            {
              english: '• Aqueous humour (between cornea-lens), vitreous humour (behind lens).',
        hindi: '• जलीय हास्य (कॉर्निया-लेंस के बीच), कांच का हास्य (लेंस के पीछे)।',
            },
            {
              english: '• **Image formation**: light → cornea → aqueous → pupil → lens → vitreous → retina. Image inverted, real, smaller. Photoreceptors → bipolar cells → ganglion cells → optic nerve → cerebral cortex.',
        hindi: '• **छवि निर्माण**: प्रकाश → कॉर्निया → जलीय → पुतली → लेंस → कांच → रेटिना। प्रतिबिम्ब उलटा, वास्तविक, छोटा। फोटोरिसेप्टर → द्विध्रुवी कोशिकाएँ → नाड़ीग्रन्थि कोशिकाएँ → ऑप्टिक तंत्रिका → सेरेब्रल कॉर्टेक्स। **कान** — तीन भाग:',
            },
            {
              english: '**Ear** — three parts:',
        hindi: '**कान** - तीन भाग:',
            },
            {
              english: '• **Outer ear** — pinna + auditory canal; ends at **tympanic membrane (eardrum)**.',
        hindi: '• **बाहरी कान** — पिन्ना + श्रवण नलिका; **टाम्पैनिक मेम्ब्रेन (कान का परदा)** पर समाप्त होता है।',
            },
            {
              english: '• **Middle ear** — 3 ossicles (malleus → incus → stapes) amplify ~22×; eustachian tube equalises pressure.',
        hindi: '• **मध्य कान** - 3 अस्थि-पंजर (मैलियस → इनकस → स्टेप्स) ~22× बढ़ते हैं; यूस्टेशियन ट्यूब दबाव को बराबर करती है।',
            },
            {
              english: '• **Inner ear** = bony labyrinth + membranous labyrinth filled with **perilymph & endolymph**. Two parts:',
        hindi: '• **आंतरिक कान** = हड्डी की भूलभुलैया + झिल्लीदार भूलभुलैया **पेरिलिम्फ और एंडोलिम्फ** से भरी हुई। दो भाग:',
            },
            {
              english: '  – **Cochlea** (snail-shaped) — **organ of Corti** with hair cells (auditory receptors) on basilar membrane; covered by tectorial membrane.',
        hindi: '- **कोक्लीअ** (घोंघे के आकार का) - **कोर्टी का अंग** बेसिलर झिल्ली पर बाल कोशिकाओं (श्रवण रिसेप्टर्स) के साथ; टेक्टोरियल झिल्ली से ढका हुआ।',
            },
            {
              english: '  – **Vestibular apparatus** — for balance: **3 semicircular canals** (dynamic equilibrium), **utricle + saccule** (static equilibrium); contain otoliths.',
        hindi: '- **वेस्टिबुलर उपकरण** - संतुलन के लिए: **3 अर्धवृत्ताकार नहरें** (गतिशील संतुलन), **यूट्रिकल + सैक्यूले** (स्थैतिक संतुलन); ओटोलिथ शामिल हैं।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Resting potential} \\approx -70\\ \\text{mV}',
      desc: {
        english: '',
        hindi: 'विश्राम क्षमता -70\\ mV',
      },
    },
    {
      latex: '\\text{Action potential threshold} \\approx -55\\ \\text{mV}; \\text{ peak} \\approx +30\\ \\text{mV}',
      desc: {
        english: '',
        hindi: 'क्रिया संभावित सीमा -55\\ mV; शिखर +30\\ mV',
      },
    },
    {
      latex: '\\text{Na}^+/K^+ \\text{ pump: 3 Na}^+ \\text{ out / 2 K}^+ \\text{ in / ATP}',
      desc: {
        english: '',
        hindi: 'Na +/K + पंप: 3 Na + आउट / 2 K + इन / एटीपी',
      },
    },
    {
      latex: '\\text{Saltatory conduction (myelinated)} \\Rightarrow \\sim 100\\ \\text{m/s}',
      desc: {
        english: '',
        hindi: 'लवणीय चालन (माइलिनेटेड) 100\\ मी/से',
      },
    },
    {
      latex: '\\text{Spinal nerves: 31 pairs; Cranial nerves: 12 pairs}',
      desc: {
        english: '',
        hindi: 'रीढ़ की हड्डी की नसें: 31 जोड़े; कपाल तंत्रिकाएँ: 12 जोड़े',
      },
    },
    {
      latex: '\\text{Rods (rhodopsin)} = \\text{dim}; \\text{ Cones (iodopsin)} = \\text{colour}',
      desc: {
        english: '',
        hindi: 'छड़ें (रोडोप्सिन) = मंद; शंकु (आयोडोप्सिन) = रंग',
      },
    },
    {
      latex: '\\text{Sympathetic = noradrenaline; Parasympathetic = ACh}',
      desc: {
        english: '',
        hindi: 'सहानुभूतिपूर्ण = नॉरएड्रेनालाईन; पैरासिम्पेथेटिक = एसीएच',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Synapse',
      problem: {
        english: 'Most common neurotransmitter at neuromuscular junction:',
        hindi: 'न्यूरोमस्कुलर जंक्शन पर सबसे आम न्यूरोट्रांसमीटर:',
      },
      answer: {
        english: 'Acetylcholine',
        hindi: 'एसिटाइलकोलाइन',
      },
      solution: {
        english: 'Released by motor neurons; binds nicotinic ACh receptors.',
        hindi: 'मोटर न्यूरॉन्स द्वारा जारी; निकोटिनिक एसीएच रिसेप्टर्स को बांधता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Eye',
      problem: {
        english: 'Visual pigment present in rods is:',
        hindi: 'छड़ों में मौजूद दृश्य वर्णक है:',
      },
      answer: {
        english: 'Rhodopsin (vit A + opsin)',
        hindi: 'रोडोप्सिन (विट ए + ऑप्सिन)',
      },
      solution: {
        english: 'Used for low-light, monochromatic vision.',
        hindi: 'कम रोशनी, मोनोक्रोमैटिक दृष्टि के लिए उपयोग किया जाता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Brain',
      problem: {
        english: 'Coordination of voluntary movements is the function of:',
        hindi: 'स्वैच्छिक आंदोलनों का समन्वय का कार्य है:',
      },
      answer: {
        english: 'Cerebellum',
        hindi: 'सेरिबैलम',
      },
      solution: {
        english: 'Also balance and posture.',
        hindi: 'संतुलन और मुद्रा भी।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Impulse',
      problem: {
        english: 'Resting potential of a neuron is maintained by:',
        hindi: 'न्यूरॉन की विश्राम क्षमता को बनाए रखा जाता है:',
      },
      answer: {
        english: 'Na⁺/K⁺ pump (3 Na⁺ out / 2 K⁺ in)',
        hindi: 'Na⁺/K⁺ पंप (3 Na⁺ बाहर / 2 K⁺ इंच)',
      },
      solution: {
        english: 'Active transport against gradients; uses ATP.',
        hindi: 'ग्रेडिएंट के विरुद्ध सक्रिय परिवहन; एटीपी का उपयोग करता है.',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Ear',
      problem: {
        english: 'Auditory receptors are present in:',
        hindi: 'श्रवण रिसेप्टर्स मौजूद हैं:',
      },
      answer: {
        english: 'Organ of Corti (cochlea)',
        hindi: 'कोर्टी का अंग (कोक्लीअ)',
      },
      solution: {
        english: 'Hair cells on basilar membrane.',
        hindi: 'बेसिलर झिल्ली पर बाल कोशिकाएँ।',
      },
    },
  ],
  next: {
    slug: 'organisms-populations',
    title: 'Organisms and Populations',
  },
};

export default chapter;
